import React from 'react'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { Tooltip } from '../Tooltip'
import { TooltipOverlayType, TooltipPositions } from '../Tooltip/Tooltip'
import { FlexAlign, FlexRow } from '../Flex'
import { PolicyWarningIcon, PolicyIcon, PolicyTitle } from './ListItem.styles'
import { LIST_ITEM_COPY } from './ListItem.constants'

export const PolicyInfo = ({
  policyName,
  isWarning,
  policyNaMismatch,
  isLarge,
}: {
  policyName: string
  isWarning: boolean
  policyNaMismatch?: boolean
  isLarge?: boolean
}): JSX.Element => {
  const icon = isWarning ? (
    <PolicyWarningIcon $isLarge={isLarge} />
  ) : (
    <PolicyIcon $isLarge={isLarge} />
  )

  return (
    <Tooltip
      id={`controls-index-row-policy-tooltip-${policyName}`}
      placement={TooltipPositions.top}
      overlayType={TooltipOverlayType.popover}
      tooltipBody={
        policyNaMismatch
          ? 'Every policy linked to this control is marked not applicable, but the control is not.'
          : ''
      }
      disabled={!policyNaMismatch}
    >
      <FlexRow
        align={FlexAlign.CENTER}
        justify={FlexAlign.FLEX_START}
        gap={4}
        isFullWidth
        style={{ minWidth: 0 }}
      >
        {icon}
        <PolicyTitle $isLarge={isLarge}>{policyName}</PolicyTitle>
      </FlexRow>
    </Tooltip>
  )
}

// A control is flagged when every policy it's linked to is not applicable but the
// control isn't. The backend decides, since linkedPoliciesList leaves out the policies
// the user can't see.
export const isPolicyNaMismatch = (
  control: Control.AsObject | undefined,
  isControlNa: boolean,
): boolean => !isControlNa && !!control?.linkedPoliciesNotApplicable

export const ControlPolicyInfo = ({
  control,
  isControlNa,
  isLarge,
}: {
  control?: Control.AsObject
  isControlNa: boolean
  isLarge?: boolean
}): JSX.Element => {
  const policyNaMismatch = isPolicyNaMismatch(control, isControlNa)
  const naPolicyLabel =
    (control?.linkedPoliciesList.length ?? 0) > 1
      ? LIST_ITEM_COPY.NOT_APPLICABLE_POLICIES
      : LIST_ITEM_COPY.NOT_APPLICABLE_POLICY
  const policyName = policyNaMismatch
    ? naPolicyLabel
    : control?.policyName || LIST_ITEM_COPY.NO_POLICY
  const isWarning = policyNaMismatch || !control?.policyName

  return (
    <PolicyInfo
      policyName={policyName}
      isWarning={isWarning}
      policyNaMismatch={policyNaMismatch}
      isLarge={isLarge}
    />
  )
}
